/// <reference types="@rspack/core/module" />

import type { VortexVersionInfo } from "./types";

/**
 * Hash of the current commit in `MercuryWorkshop/vortex` Vortex was built from.
 */
declare const COMMITHASH: string;
/**
 * Semantic version of the current Vortex build.
 */
declare const VERSION: string;

/**
 * @category Window Context
 */
export type { VortexFlags } from "./types";

/**
 * @category Window Context
 */
export type { VortexInitConfig } from "./types";

/**
 * @category Window Context
 */
export type { VortexGlobalEvent } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexGlobalDownloadEvent } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexGlobalEvents } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexDownload } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexEvent } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexEvents } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexNavigateEvent } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexUrlChangeEvent } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexContextEvent } from "./client/events";

/**
 * @category Window Context
 */
export type { VortexController } from "./controller";

/**
 * @category Window Context
 */
export type { VortexFrame } from "./controller/frame";

/**
 * @category Window Context
 */
export type { VortexClient } from "./client";

/**
 * @category Service Worker Context
 */
export type { VortexServiceWorker } from "./worker";

/**
 * @fileoverview Vortex Entry Point. This module contain global constants and factory functions to load the APIs in the bundle.
 *
 * @categoryDescription Window Context
 * APIs for the main window context, which includes creating Vortex Frames and the Controller for managing the Vortex proxy behavior in the SW.
 * @categoryDescription Service Worker Context
 * APIs designed for the service worker context, where the core logic resides. These are the essentials and include the the `VortexServiceWorker`.
 */

/**
 * Factory function that creates the `VortexController` class.
 *
 * @returns The `VortexController` class.
 *
 * @example
 * ```typescript
 * const { VortexController } = $vortexLoadController();
 *
 * const vortex = new VortexController({
 *   prefix: "/vortex/"
 * });
 *
 * await vortex.init();
 *
 * const frame = vortex.createFrame();
 * document.body.appendChild(frame.frame);
 * frame.navigate("https://example.com");
 * ```
 *
 * @category Window Context
 */
export function $vortexLoadController() {
	return require("./controller/index");
}
/**
 * Factory function that creates the `VortexClient` for controlling sandboxing.
 *
 * @returns The `VortexClient` class.
 *
 * @example
 * ```typescript
 * const VortexClient = $vortexLoadClient();
 *
 * const vortexClient = new VortexClient.VortexClient();
 * ```
 * @category Window Context
 */
export function $vortexLoadClient() {
	return require("./client/entry");
}
/**
 * Factory function that creates the `VortexServiceWorker` class.
 *
 * @returns The `VortexServiceWorker` class.
 *
 * Plain SW example
 * @example
 * ```typescript
 * // In your Service Worker
 * const { VortexServiceWorker } = $vortexLoadWorker();
 *
 * const vortex = new VortexServiceWorker();
 *
 * self.addEventListener("fetch", async (ev) => {
 *   await vortex.loadConfig();
 *
 *   if (vortex.route(ev)) {
 *     ev.respondWith(vortex.fetch(ev));
 *   }
 * });
 * ```
 *
 * Workbox-powered SW routing example
 * @example
 * ```typescript
 * // In your Service Worker (ensure you are using a bundler for Workbox)
 * // This is more useful for a webOS or if you have Offline PWA support on your proxy site
 * import { registerRoute } from 'workbox-routing';
 *
 * const { VortexServiceWorker } = $vortexLoadWorker();
 *
 * const vortex = new VortexServiceWorker();
 *
 * registerRoute(
 *   ({ request }) => {
 *     return vortex.route({ request });
 *   },
 *   async ({ event }) => {
 *     await vortex.loadConfig();
 *
 *     return vortex.fetch(event);
 *   }
 * );
 * ```
 *
 * @category Service Worker Context
 */
export function $vortexLoadWorker() {
	return require("./worker/index");
}

globalThis.$vortexRequire = function (path: string) {
	return require(path);
};

/**
 * Version information for the current Vortex build.
 *
 * @category Window Context
 */
export const $vortexVersion: VortexVersionInfo = {
	build: COMMITHASH,
	version: VERSION,
};

globalThis.$vortexLoadController = $vortexLoadController;
globalThis.$vortexLoadClient = $vortexLoadClient;
globalThis.$vortexLoadWorker = $vortexLoadWorker;
globalThis.$vortexVersion = $vortexVersion;

if ("document" in globalThis && document?.currentScript) {
	document.currentScript.remove();
}
